import { useCallback, useMemo, useState } from 'react';

// The footer's add row edits a room that does not exist yet, so it needs an id
// no real room can have.
export const NEW_ROOM_ID = 'new' as const;

export type EditingRoomId = string | null;

// Both settings lists edit rooms of this shape; the rest of each room's fields
// stay with the list that knows about them.
interface RoomLike {
  id: string;
  description: string;
}

export interface UseRoomListEditingResult<T extends RoomLike> {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  filteredRooms: T[];
  editingRoomId: EditingRoomId;
  // The row actually being edited: an id that the search has filtered out of
  // view counts as no row at all.
  effectiveEditingRoomId: EditingRoomId;
  editingName: string;
  setEditingName: (name: string) => void;
  openPickerRoomId: string | null;
  roomToDelete: T | null;
  setRoomToDelete: (room: T | null) => void;
  startEditing: (roomId: string, name: string) => void;
  startCreating: () => void;
  stopEditing: () => void;
  openPicker: (roomId: string) => void;
  closePicker: () => void;
}

// A row the search has scrolled out of view is not being edited any more. The
// add row is the exception: it belongs to the footer, not to the list.
const resolveEffectiveEditingRoomId = <T extends RoomLike>(
  editingRoomId: EditingRoomId,
  visibleRooms: T[]
): EditingRoomId => {
  if (editingRoomId === null || editingRoomId === NEW_ROOM_ID) return editingRoomId;
  return visibleRooms.some((room) => room.id === editingRoomId) ? editingRoomId : null;
};

/**
 * The editing state the room and intervention-room settings lists share:
 * the search box, which row is being renamed, which row has its needs picker
 * open, and which row is queued for deletion. Saving is not here — the two
 * lists talk to different endpoints.
 */
export function useRoomListEditing<T extends RoomLike>(rooms: T[]): UseRoomListEditingResult<T> {
  const [searchQuery, setSearchQuery] = useState('');
  const [editingRoomId, setEditingRoomId] = useState<EditingRoomId>(null);
  const [editingName, setEditingName] = useState('');
  const [openPickerRoomId, setOpenPickerRoomId] = useState<string | null>(null);
  const [roomToDelete, setRoomToDelete] = useState<T | null>(null);

  const filteredRooms = useMemo(() => {
    if (!searchQuery) return rooms;
    const query = searchQuery.toLowerCase();
    return rooms.filter((room) => room.description.toLowerCase().includes(query));
  }, [rooms, searchQuery]);

  const effectiveEditingRoomId = resolveEffectiveEditingRoomId(editingRoomId, filteredRooms);

  const startEditing = useCallback((roomId: string, name: string) => {
    setEditingName(name);
    setEditingRoomId(roomId);
    setOpenPickerRoomId(null);
  }, []);

  const startCreating = useCallback(() => {
    setEditingRoomId(NEW_ROOM_ID);
    setOpenPickerRoomId(null);
  }, []);

  const stopEditing = useCallback(() => {
    setEditingRoomId(null);
    setEditingName('');
  }, []);

  const openPicker = useCallback((roomId: string) => {
    setOpenPickerRoomId(roomId);
  }, []);

  const closePicker = useCallback(() => {
    setOpenPickerRoomId(null);
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    filteredRooms,
    editingRoomId,
    effectiveEditingRoomId,
    editingName,
    setEditingName,
    openPickerRoomId,
    roomToDelete,
    setRoomToDelete,
    startEditing,
    startCreating,
    stopEditing,
    openPicker,
    closePicker
  };
}
